import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { localDateOf } from '@servwell/shared';
import { dayLabel } from '../../lib/format';
import type { Tone } from '../ui/Badge';

/** One line's notes, typed by the router (`invoice.get`'s `lineNotes`); no cost in them. */
type LineNotes = NonNullable<
  inferOutput<TRPCOptionsProxy<AppRouter>['invoice']['get']>
>['lineNotes'][string];

/**
 * The badge beside a line that bills a part a tech used: who, and on which day, the shop's day.
 * Words only: the server decided which lines are parts used.
 */
export function partUsedLabel(
  used: NonNullable<LineNotes['part_used']>,
  tz: string,
): string {
  return `Part used · ${usedByLabel(used, tz)}`;
}

/** Who used a part and on which of the shop's days ("Devon Bright · Fri, Oct 2"). */
export function usedByLabel(
  used: { usedBy: string | null; usedAt: Date | string },
  tz: string,
): string {
  const day = dayLabel(localDateOf(new Date(used.usedAt), tz), tz);
  return used.usedBy ? `${used.usedBy} · ${day}` : day;
}

/** One badge beside a line: its words and its tone. */
export interface LineBadge {
  text: string;
  tone: Tone;
}

/**
 * Every line's badges, keyed by line id, for `LineItemsTable`; a line with no note gets none.
 * Who used the part first, then the flags the server set (Phase 1, ticket 07): No price and
 * Billed, not used are for the office to check before Issue (warn); No charge was a decision.
 */
export function lineBadges(
  notes: Record<string, LineNotes>,
  tz: string,
): Record<string, LineBadge[]> {
  return Object.fromEntries(
    Object.entries(notes).flatMap(([lineId, note]) => {
      const badges: LineBadge[] = [
        ...(note.part_used
          ? [{ text: partUsedLabel(note.part_used, tz), tone: 'neutral' as const }]
          : []),
        ...(note.no_price ? [{ text: 'No price', tone: 'warn' as const }] : []),
        ...(note.no_charge ? [{ text: 'No charge', tone: 'neutral' as const }] : []),
        ...(note.billed_not_used
          ? [
              {
                text: `Billed, not used · ${note.billed_not_used.qty}`,
                tone: 'warn' as const,
              },
            ]
          : []),
      ];
      return badges.length > 0 ? [[lineId, badges]] : [];
    }),
  );
}

/** The lines whose unit price is unknown (No price): the table says so rather than "$0.00". */
export function unpricedLines(notes: Record<string, LineNotes>): string[] {
  return Object.entries(notes).flatMap(([lineId, note]) =>
    note.no_price ? [lineId] : [],
  );
}
